// ---------------------------------------------------------------------------
// Teams Service - V11 Discovery API
// ---------------------------------------------------------------------------
// API client for V11 Discovery Teams endpoints
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'

export interface TeamsFilters {
  readonly page?: number
  readonly limit?: number
  readonly sort?: string
  readonly order?: 'asc' | 'desc'
  readonly search?: string
  readonly type?: 'team' | 'player'
  readonly country?: readonly number[]
}

export interface TeamsResponse {
  readonly success: boolean
  readonly data: readonly Record<string, unknown>[]
  readonly meta: {
    readonly page: number
    readonly limit: number
    readonly total: number
    readonly lastPage: number
    readonly hasMorePages: boolean
  }
}

export interface TeamsAggregationsResponse {
  readonly success: boolean
  readonly data: {
    readonly type: readonly { name: string; count: number }[]
    readonly countries: readonly { name: string; count: number; mentions: number }[]
    readonly cities: readonly { name: string; count: number; mentions: number }[]
    readonly topTeams: readonly { name: string; count: number }[]
    readonly topPlayers: readonly { name: string; count: number }[]
    readonly mentionTiers: readonly { name: string; count: number }[]
    readonly commentMentionTiers: readonly { name: string; count: number }[]
    readonly profileTiers: readonly { name: string; count: number }[]
    readonly totals: {
      readonly teams: number
      readonly profileMentions: number
      readonly postMentions: number
      readonly commentMentions: number
      readonly totalMentions: number
      readonly profiles: number
    }
  }
}

export interface TeamsTrendsResponse {
  readonly success: boolean
  readonly data: {
    readonly metric: string
    readonly groupBy: string
    readonly timeline: readonly Record<string, unknown>[]
  }
}

export const teamsService = {
  async getTeams(filters: TeamsFilters = {}): Promise<TeamsResponse> {
    const response = await api.get<TeamsResponse>('/api/v11/discovery/teams', {
      params: filters,
    })
    return response
  },

  async getAggregations(filters: TeamsFilters = {}): Promise<TeamsAggregationsResponse> {
    const response = await api.get<TeamsAggregationsResponse>(
      '/api/v11/discovery/teams/aggregations',
      { params: filters }
    )
    return response
  },

  async getTrends(
    metric: string,
    groupBy: string,
    filters: TeamsFilters = {}
  ): Promise<TeamsTrendsResponse> {
    const response = await api.get<TeamsTrendsResponse>(
      '/api/v11/discovery/teams/trends',
      { params: { metric, group_by: groupBy, ...filters } }
    )
    return response
  },
}
